import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Optional,
    Output,
    inject,
    input,
} from '@angular/core';

import { OfferSource } from '@cds';
import { OptionGroupType, OptionGroupTypeService } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { BetBuilderButtonSource, CrmPromoIdsMap, EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig, PriceBoostConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterButtonSize } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { PerformanceTrackerComponent } from '@frontend/sports/tracking/feature/performance-tracker';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { WINDOW } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { RxIf } from '@rx-angular/template/if';
import { BehaviorSubject } from 'rxjs';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { OptionMarketSetData } from '../event-details-common/event-details-common.models';
import { EventDetailsService } from '../event-details-common/event-details.service';
import { OptionSheetViewActions } from '../option-sheet-view/sheet-view.actions';
import { TabBarEvent } from '../tab-bar/tab-bar.models';
import { BetBuilderCorrectScoreOptionGroupComponent } from './bet-builder-correct-score-option-group.component';
import { BetBuilderPlayersOptionGroupComponent } from './bet-builder-goalscorer-option-group.component';
import { BetBuilderOverUnderOptionGroupComponent } from './bet-builder-over-under-option-group.component';
import { BetBuilderSixPackComponent } from './bet-builder-six-pack.component';
import { BetBuilderSpreadOptionGroupComponent } from './bet-builder-spread-option-group.component';
import { BetBuilderStandardGroupComponent } from './bet-builder-standard-group.component';
import { BetBuilderPlayerTeamOptionGroupComponent } from './bet-builder-teams-option-group.component';
import { CorrectScoreOptionGroupToggleViewService } from './correct-score-option-group-toggle-view.service';
import { CorrectScoreOptionGroupWrapperComponent } from './correct-score-option-group-wrapper.component';
import { GoalScorerGroupComponent } from './goal-scorer-group.component';
import { HeadToHeadOptionGroupComponent } from './head-to-head-option-group.component';
import { OptionGroupTabindexKeeperService } from './option-group-tabindex-keeper.service';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionPanelHeaderComponent } from './option-panel-header.component';
import { OutrightsOptionGroupComponent } from './outrights-option-group.component';
import { OverUnderOptionGroupComponent } from './over-under-option-group.component';
import { PeriodOptionGroupComponent } from './period-option-group.component';
import { PlayerCombinedOptionGroupComponent } from './player-combined-option-group.component';
import { PlayerMilestoneOptionGroupComponent } from './player-milestone-option-group.component';
import { PlayerMilestoneOverUnderOptionGroupComponent } from './player-milestone-over-under-option-group.component';
import { PlayerOutcomeOptionGroupComponent } from './player-outcome-option-group.component';
import { PlayerPropsOptionGroupComponent } from './player-props-option-group.component';
import { PlayerStatsOptionGroupComponent } from './player-stats-option-group.component';
import { PrecreatedBabOptionGroupComponent } from './precreated-bab-option-group.component';
import { RegularGroupComponent } from './regular-group.component';
import { ScoreMarketOptionGroupComponent } from './score-market-option-group.component';
import { ScorecastOptionGroupComponent } from './scorecast/scorecast-option-group.component';
import { WincastOptionGroupComponent } from './scorecast/wincast/wincast-option-group.component';
import { WincastTwoWayScorerOptionGroupComponent } from './scorecast/wincast/wincast-two-way-scorer-option-group.component';
import { SixPackOptionGroupComponent } from './six-pack-option-group.component';
import { SpreadOptionGroupComponent } from './spread-option-group.component';
import { TotalScoreOptionGroupComponent } from './total-score-option-group.component';
import { YesNoOptionGroupComponent } from './yes-no-option-group.component';

@Component({
    selector: 'ms-option-panel',
    templateUrl: './option-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RxIf,
        BadgeComponent,
        OptionPanelHeaderComponent,
        PerformanceTrackerComponent,
        PlayerCombinedOptionGroupComponent,
        PlayerOutcomeOptionGroupComponent,
        PlayerMilestoneOptionGroupComponent,
        PlayerMilestoneOverUnderOptionGroupComponent,
        PrecreatedBabOptionGroupComponent,
        PeriodOptionGroupComponent,
        ScorecastOptionGroupComponent,
        WincastOptionGroupComponent,
        RegularGroupComponent,
        CorrectScoreOptionGroupWrapperComponent,
        OverUnderOptionGroupComponent,
        GoalScorerGroupComponent,
        PlayerPropsOptionGroupComponent,
        SpreadOptionGroupComponent,
        WincastTwoWayScorerOptionGroupComponent,
        SixPackOptionGroupComponent,
        ScoreMarketOptionGroupComponent,
        OutrightsOptionGroupComponent,
        YesNoOptionGroupComponent,
        TotalScoreOptionGroupComponent,
        HeadToHeadOptionGroupComponent,
        BetBuilderStandardGroupComponent,
        BetBuilderSixPackComponent,
        BetBuilderOverUnderOptionGroupComponent,
        BetBuilderCorrectScoreOptionGroupComponent,
        BetBuilderSpreadOptionGroupComponent,
        BetBuilderPlayerTeamOptionGroupComponent,
        BetBuilderPlayersOptionGroupComponent,
        PlayerStatsOptionGroupComponent,
    ],
    providers: [CorrectScoreOptionGroupToggleViewService],
})
export class OptionPanelComponent implements AfterViewInit, OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() promoted: boolean;
    @Input() crmPromoted: boolean;
    @Input() optionGroup: EventOptionGroup;
    @Input() preselectedPick: { optionId: number } | null;
    @Input() thresholds?: OptionPanelThreshold[];
    @Input() scrollAdapterButtonSize: ScrollAdapterButtonSize;
    @Input() crmPromoIdsMap: CrmPromoIdsMap = { markets: {}, options: {}, templateIds: {} };
    @Input() toggleable = true;
    @Input() showHelp = false;
    @Input() collapsed = false;
    @Input() eventHelpText?: string;
    @Input() showEventName = false;
    @Input() showMarketHelp = false;
    @Input() showEventTimer = false;
    @Input() isLinkToEvent = true;
    @Input() showEventChevron = false;
    @Input() optionGroupLinkable = false;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() showMoreCounter = false;
    @Input() optionsLimitOverride?: number;
    @Input() babButtonSource?: BetBuilderButtonSource;
    @Input() betBuilderEpcotEnabled = false;
    @Input() calendarTitle?: string;
    @Input() hideMarketHeader: boolean;
    @Input() showSixPackLogosColor: boolean;
    @Input() showSixPackLogos: boolean;
    @Input() selectedSetId: string | number;
    @Input() isInSheetView = false;
    @Input() marketLogoShow = false;
    @Input() hideEventCarousel?: boolean;
    @Input() appendTooltipToWidgetSlot = false;
    @Output() collapsedChange = new EventEmitter<boolean>();
    @Output() eventTitleClicked = new EventEmitter<EventModel>();
    @Output() closeBtnClicked = new EventEmitter();
    @Output() activePeriodChange = new EventEmitter<TabBarEvent>();
    hideOptions = input<boolean>();
    optionMarketSetData = input<OptionMarketSetData>();
    showSGPBadge = input<boolean>(false);

    @HostBinding('class') className = 'option-panel';

    @HostBinding('class.precreated-bab-tab') get IsPrecreatedBabTab(): boolean {
        return this.isPrecreatedBabTab;
    }

    strategy$ = new BehaviorSubject('native');

    groupTypes = OptionGroupType;
    groupType: OptionGroupType;
    showSuspendedSelection: boolean;
    isPrecreatedBabTab = false;
    hideBetBuilderButton = false;
    activePeriodTab?: string;
    showPlayerStats = false;

    constructor(
        private typeHelper: OptionGroupTypeService,
        private elementRef: ElementRef,
        private optionGroupTrackingService: OptionGroupTrackingService,
        private eventDetailsService: EventDetailsService,
        private eventDetailsConfig: EventDetailsConfig,
        private priceBoostConfig: PriceBoostConfig,
        private store: Store,
        @Optional() private optionGroupTabKeeperService?: OptionGroupTabindexKeeperService,
    ) {
        this.showSuspendedSelection =
            this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled || this.priceBoostConfig.isEnabled || this.priceBoostConfig.isEnabledAPB;
    }

    ngOnChanges(changes: ISimpleChanges<OptionPanelComponent>): void {
        if (changes.optionGroup) {
            this.groupType = this.typeHelper.getOptionGroupType(this.optionGroup);
            this.isPrecreatedBabTab = this.eventDetailsService.isPreCreatedBABTab(this.event, this.selectedSetId);
            if (this.isPrecreatedBabTab) {
                this.hideMarketHeader = true;
                this.hideBetBuilderButton = true;
                this.crmPromoted = false;
                this.showMarketHelp = false;
            }
            // no need to maintain store when option-panel instance is used for sheet view
            if (!this.isInSheetView) {
                this.store.dispatch(OptionSheetViewActions.updateOptionSheetView({ payload: { sourceOptionGroup: this.optionGroup } }));
            }
        }

        if (changes.optionGroup || changes.event) {
            if (this.optionGroup.source === OfferSource.V2 && !this.eventDetailsConfig.enableV2LiveStats) {
                return;
            }

            this.showPlayerStats =
                this.event.live &&
                !!this.event.playerStats?.some((x) => x.source === this.optionGroup.source) &&
                !!this.optionGroup.detailedGrouping?.playerStatsProps?.length &&
                !this.isTeamStats(this.optionGroup);
        }
    }

    private isTeamStats(optionGroup: EventOptionGroup): boolean {
        return !!optionGroup.detailedGrouping?.playerStatsProps?.[0]?.split('.')[1];
    }

    ngAfterViewInit() {
        this.strategy$.next('normal');
    }

    onHeaderToggle(collapsed: boolean): void {
        this.collapsed = collapsed;
        this.collapsedChange.emit(this.collapsed);
        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            collapsed ? 'collapse' : 'expand',
            this.event.participants,
            this.optionGroup.name,
            this.optionGroup.name,
        );
    }

    onEventTitleClick(): void {
        this.eventTitleClicked.emit();
    }

    onExpandedClicked(isExpanded: boolean): void {
        if (!isExpanded && !this.isFullyVisible()) {
            this.optionPanel?.scrollIntoView(true);
        }
    }

    onCloseButtonClicked(): void {
        this.closeBtnClicked.emit();
    }

    onActivePeriodChange(activeTab$: TabBarEvent): void {
        this.activePeriodTab = activeTab$.active.attribute;
        this.activePeriodChange.emit(activeTab$);
    }

    isFullyVisible(): boolean {
        const { top, bottom } = this.optionPanel?.getBoundingClientRect() ?? {};

        return top >= 0 && bottom <= this._window.innerHeight;
    }

    private get optionPanel(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    openSheetView(): void {
        this.optionGroupTrackingService.trackEvents({
            label: this.event.sport.name,
            actionEvent: trackingConstants.EVENT_CLICK,
            participants: this.event.participants,
            locationEvent: this.getLocationEvent(),
            eventDetailText: trackingConstants.SEE_ALL,
            event: trackingConstants.EVENT_TRACKING,
            sportId: this.event.sport.id,
            leagueId: this.event.league.id,
        });

        this.store.dispatch(
            OptionSheetViewActions.openOptionSheetView({
                payload: {
                    tabServiceInjector: this.optionGroupTabKeeperService,
                    data: {
                        showMarketHelp: this.showMarketHelp,
                        sourceEvent: this.event,
                        sourceOptionGroup: this.optionGroup,
                        promoted: this.promoted,
                        crmPromoted: this.crmPromoted,
                        crmPromoIdsMap: this.crmPromoIdsMap,
                        tracking: this.tracking,
                        showMoreCounter: this.showMoreCounter,
                        optionsLimitOverride: this.optionsLimitOverride,
                        showHelp: this.showHelp,
                        thresholds: this.thresholds,
                        trackingModuleType: this.trackingModuleType,
                        betBuilderEpcotEnabled: this.betBuilderEpcotEnabled,
                        isInSheetView: true,
                        sourceOfPick: this.sourceOfPick,
                        showEventName: this.showEventName,
                        showEventTimer: this.showEventTimer,
                        toggleable: this.toggleable,
                        isLinkToEvent: this.isLinkToEvent,
                        showEventChevron: this.showEventChevron,
                        babButtonSource: this.babButtonSource,
                        hideMarketHeader: this.hideMarketHeader,
                        showSixPackLogosColor: this.showSixPackLogosColor,
                        showSixPackLogos: this.showSixPackLogos,
                        selectedSetId: this.selectedSetId,
                        groupType: this.groupType,
                        hideEventCarousel: this.hideEventCarousel,
                        optionMarketSetData: this.optionMarketSetData(),
                    },
                },
            }),
        );
    }

    getLocationEvent(): string {
        let locationEventInfo = this.optionGroup.name;
        const trackingInfo = this.tracking?.split('/')[0];
        if (trackingInfo) {
            locationEventInfo = `${trackingInfo}-${locationEventInfo}`;
        }

        return locationEventInfo;
    }

    private readonly _window = inject(WINDOW);
}
